import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, IconButton, Button,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar,
  Tooltip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TablePagination, Switch, FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon,
  Close as CloseIcon, Refresh as RefreshIcon, AccountBalance as AccountBalanceIcon,
  CompareArrows as ReconcileIcon, CheckCircle as CheckCircleIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { usePageBreakpoints, pageContainerSx } from '../../hooks/useClinicPageBreakpoints';


const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const emptyAccount = {
  bank_name: '',
  bank_code: '',
  agency: '',
  account_number: '',
  account_type: 'corrente',
  holder_name: '',
  holder_cpf_cnpj: '',
  initial_balance: 0,
  is_default: false,
  notes: '',
};

const emptyEntry = {
  entry_date: '',
  description: '',
  amount: 0,
  entry_type: 'credit' as 'credit' | 'debit',
  reference_code: '',
  notes: '',
};

const BankStatements: React.FC = () => {
  const { isMobile } = usePageBreakpoints();

  // Bank accounts state
  const [accounts, setAccounts] = useState<any[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
  const [accountForm, setAccountForm] = useState({ ...emptyAccount });
  const [savingAccount, setSavingAccount] = useState(false);

  // Statements state
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [entries, setEntries] = useState<any[]>([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterReconciled, setFilterReconciled] = useState('');

  // Entry dialog
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [entryForm, setEntryForm] = useState({ ...emptyEntry });
  const [savingEntry, setSavingEntry] = useState(false);

  // Reconcile dialog
  const [reconcileDialogOpen, setReconcileDialogOpen] = useState(false);
  const [reconcileEntryId, setReconcileEntryId] = useState('');
  const [reconcileTransactionId, setReconcileTransactionId] = useState('');
  const [savingReconcile, setSavingReconcile] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const ofxInputRef = useRef<HTMLInputElement>(null);
  const [importingOfx, setImportingOfx] = useState(false);

  const showMessage = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // ─── Bank Accounts ──────────────────────────────────────────────

  const fetchAccounts = useCallback(async () => {
    try {
      setLoadingAccounts(true);
      const res = await axios.get(getApiUrl('/financial-module/bank-accounts'));
      setAccounts(res.data?.data || []);
    } catch (err) {
      console.error(err);
      showMessage('Erro ao carregar contas bancárias', 'error');
    } finally {
      setLoadingAccounts(false);
    }
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  useEffect(() => {
    if (accounts.length > 0 && !selectedAccountId) {
      const defaultAcc = accounts.find((a: any) => a.is_default);
      setSelectedAccountId(defaultAcc?.id || accounts[0].id);
    }
  }, [accounts, selectedAccountId]);

  const openNewAccount = () => {
    setEditingAccountId(null);
    setAccountForm({ ...emptyAccount });
    setAccountDialogOpen(true);
  };

  const openEditAccount = (acc: any) => {
    setEditingAccountId(acc.id);
    setAccountForm({
      bank_name: acc.bank_name || '',
      bank_code: acc.bank_code || '',
      agency: acc.agency || '',
      account_number: acc.account_number || '',
      account_type: acc.account_type || 'corrente',
      holder_name: acc.holder_name || '',
      holder_cpf_cnpj: acc.holder_cpf_cnpj || '',
      initial_balance: acc.initial_balance || 0,
      is_default: acc.is_default || false,
      notes: acc.notes || '',
    });
    setAccountDialogOpen(true);
  };

  const handleSaveAccount = async () => {
    try {
      setSavingAccount(true);
      if (editingAccountId) {
        await axios.put(getApiUrl(`/financial-module/bank-accounts/${editingAccountId}`), accountForm);
        showMessage('Conta atualizada com sucesso');
      } else {
        await axios.post(getApiUrl('/financial-module/bank-accounts'), accountForm);
        showMessage('Conta criada com sucesso');
      }
      setAccountDialogOpen(false);
      fetchAccounts();
    } catch (err: any) {
      showMessage(err.response?.data?.error || 'Erro ao salvar conta', 'error');
    } finally {
      setSavingAccount(false);
    }
  };

  const handleDeleteAccount = async (id: string) => {
    if (!window.confirm('Excluir esta conta bancária?')) return;
    try {
      await axios.delete(getApiUrl(`/financial-module/bank-accounts/${id}`));
      showMessage('Conta excluída');
      if (selectedAccountId === id) setSelectedAccountId('');
      fetchAccounts();
    } catch (err: any) {
      showMessage(err.response?.data?.error || 'Erro ao excluir conta', 'error');
    }
  };

  // ─── Statements ─────────────────────────────────────────────────

  const buildEntryParams = useCallback(() => {
    const params: Record<string, string | number> = {
      page: page + 1,
      limit: rowsPerPage,
    };
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (filterReconciled === 'true') params.is_reconciled = 1;
    if (filterReconciled === 'false') params.is_reconciled = 0;
    return params;
  }, [page, rowsPerPage, dateFrom, dateTo, filterReconciled]);

  const fetchEntries = useCallback(async () => {
    if (!selectedAccountId) return;
    try {
      setLoadingEntries(true);
      const params = buildEntryParams();
      const res = await axios.get(
        getApiUrl(`/financial-module/bank-accounts/${selectedAccountId}/statements`),
        { params },
      );
      setEntries(res.data?.data || []);
      setTotalEntries(res.data?.total ?? 0);
    } catch (err) {
      console.error(err);
      showMessage('Erro ao carregar extratos', 'error');
    } finally {
      setLoadingEntries(false);
    }
  }, [selectedAccountId, buildEntryParams]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const openNewEntry = () => {
    setEntryForm({ ...emptyEntry });
    setEntryDialogOpen(true);
  };

  const handleSaveEntry = async () => {
    try {
      setSavingEntry(true);
      await axios.post(
        getApiUrl(`/financial-module/bank-accounts/${selectedAccountId}/statements`),
        entryForm,
      );
      showMessage('Lançamento criado com sucesso');
      setEntryDialogOpen(false);
      fetchEntries();
      fetchAccounts();
    } catch (err: any) {
      showMessage(err.response?.data?.error || 'Erro ao salvar lançamento', 'error');
    } finally {
      setSavingEntry(false);
    }
  };

  const handleImportOfxClick = () => {
    if (!selectedAccountId) {
      showMessage('Selecione uma conta bancária', 'error');
      return;
    }
    ofxInputRef.current?.click();
  };

  const handleOfxFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !selectedAccountId) return;
    if (!file.name.toLowerCase().endsWith('.ofx')) {
      showMessage('Selecione um arquivo .ofx', 'error');
      return;
    }
    try {
      setImportingOfx(true);
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(
        getApiUrl(`/financial-module/bank-accounts/${selectedAccountId}/statements/import-ofx`),
        formData,
      );
      const d = res.data?.data;
      const skip = typeof d?.skipped_duplicates === 'number' ? d.skipped_duplicates : 0;
      const imported = typeof d?.imported === 'number' ? d.imported : 0;
      const curBal = d?.current_balance != null ? Number(d.current_balance) : null;
      if (d) {
        let msg = `Importação OFX: ${imported} lançamento(s) incluído(s)`;
        if (skip > 0) msg += `. ${skip} ignorado(s) (já existentes)`;
        if (curBal != null && Number.isFinite(curBal)) {
          msg += `. Saldo atual da conta: ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(curBal)}`;
        }
        showMessage(`${msg}.`);
      } else {
        showMessage('Importação OFX concluída');
      }
      fetchEntries();
      fetchAccounts();
    } catch (err: any) {
      showMessage(err.response?.data?.error || 'Erro ao importar OFX', 'error');
    } finally {
      setImportingOfx(false);
    }
  };

  // ─── Reconciliation ─────────────────────────────────────────────

  const openReconcile = (entryId: string) => {
    setReconcileEntryId(entryId);
    setReconcileTransactionId('');
    setReconcileDialogOpen(true);
  };

  const handleReconcile = async () => {
    try {
      setSavingReconcile(true);
      await axios.post(
        getApiUrl(`/financial-module/bank-accounts/${selectedAccountId}/reconcile`),
        { entry_id: reconcileEntryId, transaction_id: reconcileTransactionId },
      );
      showMessage('Conciliação realizada com sucesso');
      setReconcileDialogOpen(false);
      fetchEntries();
    } catch (err: any) {
      showMessage(err.response?.data?.error || 'Erro na conciliação', 'error');
    } finally {
      setSavingReconcile(false);
    }
  };

  const formatDate = (value: string) => {
    if (!value) return '-';
    try {
      return new Date(value).toLocaleDateString('pt-BR');
    } catch {
      return value;
    }
  };

  return (
    <Box sx={pageContainerSx}>
      {/* ─── Header ─────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
        <AccountBalanceIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h5" fontWeight="bold">Extratos Bancários</Typography>
      </Box>

      {/* ─── Bank Accounts Section ──────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <Typography variant="h6">Contas Bancárias</Typography>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNewAccount}>
          Nova Conta
        </Button>
        <Tooltip title="Atualizar">
          <IconButton onClick={fetchAccounts} color="primary"><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      {loadingAccounts ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : accounts.length === 0 ? (
        <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">Nenhuma conta bancária cadastrada</Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1, mb: 3 }}>
          {accounts.map((acc: any) => (
            <Card
              key={acc.id}
              sx={{
                minWidth: 240,
                cursor: 'pointer',
                border: selectedAccountId === acc.id ? '2px solid' : '1px solid',
                borderColor: selectedAccountId === acc.id ? 'primary.main' : 'divider',
              }}
              onClick={() => { setSelectedAccountId(acc.id); setPage(0); }}
            >
              <CardContent sx={{ pb: '12px !important' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold" noWrap>
                    {acc.bank_name}
                  </Typography>
                  <Box>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditAccount(acc); }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteAccount(acc.id); }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Conta: {acc.account_number}
                </Typography>
                <Typography variant="h6" sx={{ mt: 1, color: (acc.current_balance ?? 0) >= 0 ? 'success.main' : 'error.main' }}>
                  {formatCurrency(acc.current_balance ?? 0)}
                </Typography>
                {acc.is_default && <Chip label="Padrão" size="small" color="primary" sx={{ mt: 1 }} />}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* ─── Account Dialog ─────────────────────────── */}
      <Dialog fullScreen={isMobile} scroll="paper" open={accountDialogOpen} onClose={() => setAccountDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAccountId ? 'Editar Conta Bancária' : 'Nova Conta Bancária'}
          <IconButton onClick={() => setAccountDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={8}>
              <TextField fullWidth label="Nome do Banco" value={accountForm.bank_name} onChange={(e) => setAccountForm({ ...accountForm, bank_name: e.target.value })} size="small" />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="Código Banco" value={accountForm.bank_code} onChange={(e) => setAccountForm({ ...accountForm, bank_code: e.target.value })} size="small" />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="Agência" value={accountForm.agency} onChange={(e) => setAccountForm({ ...accountForm, agency: e.target.value })} size="small" />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="Número da Conta" value={accountForm.account_number} onChange={(e) => setAccountForm({ ...accountForm, account_number: e.target.value })} size="small" />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select value={accountForm.account_type} label="Tipo" onChange={(e) => setAccountForm({ ...accountForm, account_type: e.target.value })}>
                  <MenuItem value="corrente">Corrente</MenuItem>
                  <MenuItem value="poupança">Poupança</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Titular" value={accountForm.holder_name} onChange={(e) => setAccountForm({ ...accountForm, holder_name: e.target.value })} size="small" />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="CPF/CNPJ do Titular" value={accountForm.holder_cpf_cnpj} onChange={(e) => setAccountForm({ ...accountForm, holder_cpf_cnpj: e.target.value })} size="small" />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Saldo Inicial" type="number" value={accountForm.initial_balance} onChange={(e) => setAccountForm({ ...accountForm, initial_balance: parseFloat(e.target.value) || 0 })} size="small" InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
            </Grid>
            <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={<Switch checked={accountForm.is_default} onChange={(e) => setAccountForm({ ...accountForm, is_default: e.target.checked })} />}
                label="Conta Padrão"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Observações" value={accountForm.notes} onChange={(e) => setAccountForm({ ...accountForm, notes: e.target.value })} size="small" multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccountDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveAccount} disabled={savingAccount || !accountForm.bank_name || !accountForm.account_number}>
            {savingAccount ? <CircularProgress size={20} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Statements Section ─────────────────────── */}
      {selectedAccountId && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h6">Extrato</Typography>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Conta</InputLabel>
              <Select value={selectedAccountId} label="Conta" onChange={(e) => { setSelectedAccountId(e.target.value); setPage(0); }}>
                {accounts.map((a: any) => (
                  <MenuItem key={a.id} value={a.id}>{a.bank_name} - {a.account_number}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField size="small" type="date" label="De" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(0); }} InputLabelProps={{ shrink: true }} sx={{ width: 155 }} />
            <TextField size="small" type="date" label="Até" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(0); }} InputLabelProps={{ shrink: true }} sx={{ width: 155 }} />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Conciliação</InputLabel>
              <Select value={filterReconciled} label="Conciliação" onChange={(e) => { setFilterReconciled(e.target.value); setPage(0); }}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="true">Conciliados</MenuItem>
                <MenuItem value="false">Pendentes</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ flex: 1 }} />
            <input
              ref={ofxInputRef}
              type="file"
              accept=".ofx,application/ofx,text/plain"
              hidden
              onChange={handleOfxFileSelected}
            />
            <Button
              variant="outlined"
              startIcon={importingOfx ? <CircularProgress size={18} /> : <UploadFileIcon />}
              onClick={handleImportOfxClick}
              disabled={importingOfx || loadingAccounts}
            >
              Importar OFX
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openNewEntry}>
              Novo Lançamento
            </Button>
            <Tooltip title="Atualizar">
              <IconButton onClick={fetchEntries} color="primary"><RefreshIcon /></IconButton>
            </Tooltip>
          </Box>

          <TableContainer component={Paper}>
            {loadingEntries ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : entries.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography color="text.secondary">Nenhum lançamento encontrado</Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Data</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Descrição</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Valor</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Saldo</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Conciliado</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((entry: any, idx: number) => (
                    <TableRow key={entry.id || idx} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(entry.entry_date)}</TableCell>
                      <TableCell>{entry.description || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={entry.entry_type === 'credit' ? 'Crédito' : 'Débito'}
                          size="small"
                          color={entry.entry_type === 'credit' ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ color: entry.entry_type === 'credit' ? 'success.main' : 'error.main', fontWeight: 500 }}>
                        {entry.entry_type === 'debit' ? '- ' : '+ '}{formatCurrency(Math.abs(entry.amount ?? 0))}
                      </TableCell>
                      <TableCell align="right">{formatCurrency(entry.balance ?? 0)}</TableCell>
                      <TableCell align="center">
                        {entry.is_reconciled ? (
                          <CheckCircleIcon fontSize="small" color="success" />
                        ) : (
                          <Chip label="Pendente" size="small" variant="outlined" color="warning" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {!entry.is_reconciled && (
                          <Tooltip title="Conciliar">
                            <IconButton size="small" color="primary" onClick={() => openReconcile(entry.id)}>
                              <ReconcileIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <TablePagination
              component="div"
              count={totalEntries}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Linhas por página"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`}
            />
          </TableContainer>
        </>
      )}

      {/* ─── New Entry Dialog ───────────────────────── */}
      <Dialog fullScreen={isMobile} scroll="paper" open={entryDialogOpen} onClose={() => setEntryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Novo Lançamento
          <IconButton onClick={() => setEntryDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <TextField fullWidth label="Data" type="date" value={entryForm.entry_date} onChange={(e) => setEntryForm({ ...entryForm, entry_date: e.target.value })} size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select value={entryForm.entry_type} label="Tipo" onChange={(e) => setEntryForm({ ...entryForm, entry_type: e.target.value as 'credit' | 'debit' })}>
                  <MenuItem value="credit">Crédito</MenuItem>
                  <MenuItem value="debit">Débito</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Descrição" value={entryForm.description} onChange={(e) => setEntryForm({ ...entryForm, description: e.target.value })} size="small" />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Valor" type="number" value={entryForm.amount} onChange={(e) => setEntryForm({ ...entryForm, amount: parseFloat(e.target.value) || 0 })} size="small" InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Código Referência" value={entryForm.reference_code} onChange={(e) => setEntryForm({ ...entryForm, reference_code: e.target.value })} size="small" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Observações" value={entryForm.notes} onChange={(e) => setEntryForm({ ...entryForm, notes: e.target.value })} size="small" multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEntryDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveEntry} disabled={savingEntry || !entryForm.entry_date || !entryForm.amount}>
            {savingEntry ? <CircularProgress size={20} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Reconcile Dialog ───────────────────────── */}
      <Dialog fullScreen={isMobile} scroll="paper" open={reconcileDialogOpen} onClose={() => setReconcileDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Conciliar Lançamento
          <IconButton onClick={() => setReconcileDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Informe o ID da transação financeira correspondente para conciliar este lançamento.
          </Typography>
          <TextField
            fullWidth
            label="ID da Transação"
            value={reconcileTransactionId}
            onChange={(e) => setReconcileTransactionId(e.target.value)}
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReconcileDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleReconcile} disabled={savingReconcile || !reconcileTransactionId}>
            {savingReconcile ? <CircularProgress size={20} /> : 'Conciliar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Snackbar ───────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BankStatements;
